import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Virtuoso } from 'react-virtuoso';
import { useMediaQuery } from '@context/mediaQuery';
import type { PlaylistRelease } from '../Page/store';
import { PreviewRow } from './PreviewRow';
import { getRowSkeletons } from './Skeleton';
import { MAX_MINIMIZED_RELEASES } from '.';

export const PreviewReleases = ({
  loading,
  hasNextPage,
  username,
  releaseCount,
  releases,
  loadMore,
  playlistId,
  isExpanded,
}: {
  hasNextPage: boolean;
  loading: boolean;
  username: string;
  releaseCount: number;
  releases: PlaylistRelease[];
  loadMore: () => void;
  playlistId: string;
  isExpanded: boolean;
}) => {
  const renderRow = React.useCallback(
    (_: number, releaseFragment: PlaylistRelease) => (
      <PreviewRow
        key={releaseFragment.id}
        releaseFrag={releaseFragment}
        username={username}
        newRelease={false}
        playlistId={playlistId}
      />
    ),
    [username, playlistId],
  );

  const [scrollParent, setScrollParent] = useState<HTMLDivElement | undefined | null>(undefined);
  const { tablet } = useMediaQuery();

  // If on mobile or there is aren't enough to show the view more button just skip virtuoso
  const showExpandedReleases = tablet && releaseCount > MAX_MINIMIZED_RELEASES;
  const collapsedRenderedReleases = useMemo(
    () => releases.map((release, index) => renderRow(index, release)),
    [releases, renderRow],
  );

  useEffect(() => {
    //scrolls the component to the top when the component is closed to avoid weird cutoffs
    if (!isExpanded && scrollParent?.scrollTop !== 0) {
      scrollParent?.scrollTo(0, 0);
    }
  }, [isExpanded, scrollParent]);

  const Footer = useCallback(() => {
    return (
      <div className="h-fit min-h-[4px]">
        {loading &&
          getRowSkeletons(Math.min(MAX_MINIMIZED_RELEASES, releaseCount - releases.length))}
      </div>
    );
  }, [loading, releaseCount, releases]);

  return (
    <div
      className={clsx(
        'scrollbar-transparent hover:scrollbar-light relative flex w-full flex-col overflow-auto py-2 [scrollbar-gutter:stable]',
        isExpanded ? 'overflow-auto' : 'overflow-hidden',
      )}
      ref={setScrollParent}
    >
      <div className="flex flex-col">
        {showExpandedReleases ? (
          <Virtuoso
            data={releases}
            itemContent={renderRow}
            customScrollParent={scrollParent || undefined}
            endReached={isExpanded && hasNextPage ? loadMore : undefined}
            components={{ Footer }}
            fixedItemHeight={56}
          />
        ) : loading ? (
          getRowSkeletons(releaseCount)
        ) : (
          collapsedRenderedReleases
        )}
      </div>
    </div>
  );
};
